// src/features/clients/pages/CheckinApprovalsPage.tsx
// Visitas por aprobar del registro por QR (F1, jul-2026).
//
// El cliente escanea el QR del mostrador y se registra solo desde SU teléfono: el alta
// entra al instante, pero los puntos esperan aquí. Así un negocio con un solo empleado
// no tiene que capturar datos ni prestar su equipo — solo confirmar con un toque.
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../../components/layout/Layout';
import { useAuth } from '../../../context/AuthContext';
import { useError } from '../../../context/ErrorContext';
import { checkinRequestsApi, type CheckinRequest } from '../../../api/checkinRequestsApi';
import QrCheckinCard from '../../../components/qr/QrCheckinCard';
import { NoAcumulaChip } from '../components/AccrualBlockModal';
import { useAccrualBlockMap } from '../../../api/accrualBlocksApi';
import DateTimeDisplay from '../../../components/common/DateTimeDisplay';
import {
  QrCode, Check, X, Loader2, UserPlus, RefreshCw, CheckCheck, Clock, Receipt,
  Laugh, Smile, Meh, Frown,
} from 'lucide-react';

// Carita con la que el cliente calificó su visita al registrarse por QR (30-oct-2026). Mismos colores que Últimas visitas.
const CARITAS: Record<string, { label: string; color: string; Icon: typeof Smile }> = {
  excelente: { label: 'Excelente', color: 'text-yellow-600 dark:text-yellow-400', Icon: Laugh },
  bueno: { label: 'Buena', color: 'text-green-600 dark:text-green-400', Icon: Smile },
  neutral: { label: 'Neutral', color: 'text-blue-600 dark:text-blue-400', Icon: Meh },
  malo: { label: 'Mala', color: 'text-red-600 dark:text-red-400', Icon: Frown },
};

const CaritaChip: React.FC<{ feedback?: string | null }> = ({ feedback }) => {
  const c = feedback ? CARITAS[feedback] : undefined;
  if (!c) return null;
  return (
    <span className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded bg-surface-2 ${c.color}`} data-testid="carita-qr" title="Calificación que dio el cliente al registrarse por QR">
      <c.Icon className="h-3 w-3 mr-1" />
      {c.label}
    </span>
  );
};
import { BaseApiError } from '../../../api/baseClient';

const CheckinApprovalsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const noAcumula = useAccrualBlockMap();   // «Quién no acumula» (19-oct-2026)
  const { addError, clearErrors } = useError();
  // En cashback los puntos son un % del consumo: el QR no sabe cuánto gastó el cliente,
  // así que el importe se teclea aquí al confirmar. En modo visits no se pide nada.
  const isCashback = (currentUser?.loyaltyMode || 'visits') === 'cashback';
  const [amounts, setAmounts] = useState<Record<number, string>>({});
  // Folio del ticket (23-oct-2026, Hogar China City): mismo modo y misma regla que RegisterVisitModal. Con 'off' no
  // aparece nada; con 'required' no se confirma (ni una ni «todas») sin folio. Sale en cashback Y en visits.
  const modoFolio = currentUser?.ticketRef || 'off';
  const [folios, setFolios] = useState<Record<number, string>>({});
  const folioDe = (id: number) => (folios[id] || '').trim();
  const nombreDe = (r: CheckinRequest) => `${r.first_name}${r.last_name ? ` ${r.last_name}` : ''}`.trim();
  const [requests, setRequests] = useState<CheckinRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [flash, setFlash] = useState('');

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await checkinRequestsApi.list('pending', 100);
      setRequests(r.requests || []);
    } catch {
      if (!silent) addError('No se pudieron cargar las visitas pendientes.', 'unknown', 'checkin-approvals');
    } finally {
      setLoading(false);
    }
  }, [addError]);

  useEffect(() => { load(); }, [load]);

  // Refresco en segundo plano: el empleado deja la pantalla abierta mientras atiende.
  useEffect(() => {
    const t = window.setInterval(() => load(true), 30000);
    return () => window.clearInterval(t);
  }, [load]);

  const showFlash = (msg: string) => { setFlash(msg); window.setTimeout(() => setFlash(''), 4000); };

  const handleApprove = async (req: CheckinRequest) => {
    clearErrors();
    const amount = isCashback ? Number(amounts[req.id] || 0) : 0;
    if (isCashback && !(amount > 0)) {
      addError('Escribe el monto de la venta para calcular sus puntos.', 'validation', 'checkin-amount');
      return;
    }
    if (modoFolio === 'required' && !folioDe(req.id)) {
      addError(`Captura el folio del ticket de ${nombreDe(req)}.`, 'validation', 'checkin-folio');
      return;
    }
    setBusyId(req.id);
    try {
      const r = await checkinRequestsApi.approve(req.id, amount,
        modoFolio !== 'off' && folioDe(req.id) ? folioDe(req.id) : undefined);
      setRequests(prev => prev.filter(x => x.id !== req.id));
      showFlash(r.accrual_blocked
        ? `Visita de ${req.first_name} registrada sin puntos: no acumula (${r.accrual_blocked.reason_label || r.accrual_blocked.reason})`
        : `Visita de ${req.first_name} confirmada · +${r.points_added ?? 0} pts`);
    } catch (e: unknown) {
      // El motor rechaza, por ejemplo, si el cliente ya registró visita dentro de la ventana.
      const msg = (e as { message?: string })?.message || '';
      const code = e instanceof BaseApiError ? (e.data as { code?: string; reason?: string } | null)?.code
        || (e.data as { reason?: string } | null)?.reason : undefined;
      addError(
        msg.includes('esperar') || msg.includes('TIME_RESTRICTION') || code === 'TIME_RESTRICTION'
          ? `${req.first_name} registró una visita hace muy poco; espera a que pase el tiempo mínimo entre visitas.`
          : code && code.startsWith('TICKET_REF')
            ? msg   // folio: obligatorio, inválido o ya registrado (el servidor dice cuándo y a quién)
            : 'No se pudo confirmar la visita. Inténtalo de nuevo.',
        code && code.startsWith('TICKET_REF') ? 'validation' : 'unknown', 'checkin-approve');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (req: CheckinRequest) => {
    clearErrors();
    setBusyId(req.id);
    try {
      await checkinRequestsApi.reject(req.id);
      setRequests(prev => prev.filter(x => x.id !== req.id));
      showFlash(`Solicitud de ${req.first_name} descartada`);
    } catch {
      addError('No se pudo descartar la solicitud.', 'unknown', 'checkin-reject');
    } finally {
      setBusyId(null);
    }
  };

  const handleApproveAll = async () => {
    clearErrors();
    if (modoFolio === 'required') {
      const faltan = requests.filter(r => !folioDe(r.id));
      if (faltan.length) {
        addError(`Falta el folio del ticket de: ${faltan.map(nombreDe).join(', ')}.`, 'validation', 'checkin-folio');
        return;
      }
    }
    const refs: Record<number, string> = {};
    if (modoFolio !== 'off') requests.forEach(r => { if (folioDe(r.id)) refs[r.id] = folioDe(r.id); });
    setBulkBusy(true);
    try {
      const r = await checkinRequestsApi.approveAll(modoFolio !== 'off' ? refs : undefined);
      await load(true);
      const porFolio = r.failed.filter(f => (f.code || f.reason || '').startsWith('TICKET_REF'));
      if (porFolio.length) {
        addError(porFolio.map(f => f.error || f.message).filter(Boolean).join(' · '), 'validation', 'checkin-folio');
      }
      showFlash(
        r.failed.length
          ? `${r.approved} confirmadas · ${r.failed.length} quedaron pendientes${porFolio.length ? '' : ' (tiempo mínimo entre visitas)'}`
          : `${r.approved} visitas confirmadas`);
    } catch (e: unknown) {
      const d = e instanceof BaseApiError ? (e.data as { code?: string; missing?: number[]; error?: string } | null) : null;
      if (d?.code === 'TICKET_REF_REQUIRED') {
        // Llegó otra solicitud después de cargar la pantalla (o a alguna le faltaba): se recarga y se dice cuáles.
        await load(true);
        const nombres = (d.missing || []).map(id => requests.find(r => r.id === id)).filter(Boolean).map(r => nombreDe(r as CheckinRequest));
        addError(nombres.length ? `Falta el folio del ticket de: ${nombres.join(', ')}.`
          : 'Llegaron visitas nuevas sin folio: captura su folio y vuelve a confirmar.', 'validation', 'checkin-folio');
      } else if (d?.code === 'BAD_TICKET_REF') {
        addError(d.error || 'Folio inválido.', 'validation', 'checkin-folio');
      } else {
        addError('No se pudieron confirmar las visitas.', 'unknown', 'checkin-approve-all');
      }
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <Layout>
      <div className="w-full">

        {/* Cabecera del QR (8-oct-2026): apagado invita a activarlo; prendido da el QR y el cartel. */}
        <div className="mb-6"><QrCheckinCard variante="completa" /></div>

        {/* ── Encabezado ── */}
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-content flex items-center gap-2">
              <QrCode className="h-6 w-6 text-accent" />
              Visitas por QR
            </h1>
            <p className="text-sm text-content-3 mt-1">
              Clientes que se registraron escaneando tu código QR. Confirma para acreditarles sus puntos.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => load()}
              className="inline-flex items-center px-3 py-2 border border-line rounded-md text-sm text-content-2 bg-surface hover:bg-surface-2"
            >
              <RefreshCw className="h-4 w-4 mr-1.5" />
              Actualizar
            </button>
            {requests.length > 1 && !isCashback && (
              <button
                onClick={handleApproveAll}
                disabled={bulkBusy}
                className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white bg-accent hover:brightness-110 disabled:opacity-50"
              >
                {bulkBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCheck className="h-4 w-4 mr-1.5" />}
                Confirmar todas ({requests.length})
              </button>
            )}
          </div>
        </div>

        {flash && (
          <div className="mb-4 p-3 rounded-lg bg-green-50 dark:bg-green-500/10 border-l-4 border-green-400 text-green-700 dark:text-green-300 text-sm">
            {flash}
          </div>
        )}

        {/* ── Lista ── */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-accent" />
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-16 bg-surface rounded-lg shadow">
            <Clock className="h-10 w-10 mx-auto text-content-3 mb-3" />
            <p className="text-content font-medium">No hay visitas por confirmar</p>
            <p className="text-sm text-content-3 mt-1">
              Cuando un cliente escanee tu QR y se registre, aparecerá aquí.
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {requests.map(req => (
              <li key={req.id} className="bg-surface shadow rounded-lg p-4 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-content">
                      {req.first_name} {req.last_name || ''}
                    </span>
                    <NoAcumulaChip block={noAcumula.get(String(req.contact_id))} />
                    <CaritaChip feedback={req.feedback} />
                    {req.is_new_contact === 1 && (
                      <span className="inline-flex items-center text-[11px] px-2 py-0.5 rounded bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300">
                        <UserPlus className="h-3 w-3 mr-1" />
                        Cliente nuevo
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-content-3 mt-0.5">{req.phone}</p>
                  <p className="text-xs text-content-3 mt-1">
                    <DateTimeDisplay date={new Date(req.created_at)} />
                    {req.branch_name ? ` · ${req.branch_name}` : ''}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {isCashback && (
                    <div className="relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-3 text-sm">$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        placeholder="Monto"
                        value={amounts[req.id] || ''}
                        onChange={e => setAmounts(prev => ({ ...prev, [req.id]: e.target.value }))}
                        className="w-28 pl-6 pr-2 py-2 text-sm border border-line rounded-md bg-surface text-content"
                      />
                    </div>
                  )}
                  {modoFolio !== 'off' && (
                    <div className="relative">
                      <Receipt size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-3" />
                      <input
                        value={folios[req.id] || ''}
                        onChange={e => setFolios(prev => ({ ...prev, [req.id]: e.target.value }))}
                        maxLength={40}
                        data-testid="qr-folio"
                        aria-label={modoFolio === 'required' ? 'Folio del ticket' : 'Folio del ticket (opcional)'}
                        title={modoFolio === 'required' ? 'Folio del ticket' : 'Folio del ticket (opcional)'}
                        placeholder={modoFolio === 'required' ? 'Folio' : 'Folio (opcional)'}
                        className="w-36 pl-7 pr-2 py-2 text-sm border border-line rounded-md bg-surface text-content"
                      />
                    </div>
                  )}
                  <button
                    onClick={() => handleReject(req)}
                    disabled={busyId === req.id}
                    className="inline-flex items-center px-3 py-2 border border-line rounded-md text-sm text-content-2 bg-surface hover:bg-surface-2 disabled:opacity-50"
                    title="Descartar (no acredita puntos)"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleApprove(req)}
                    disabled={busyId === req.id}
                    className="inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-accent hover:brightness-110 disabled:opacity-50"
                  >
                    {busyId === req.id
                      ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                      : <Check className="h-4 w-4 mr-1.5" />}
                    Confirmar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Layout>
  );
};

export default CheckinApprovalsPage;
